import React from 'react';
import type { Quest } from '../../../../../shared/NPCData';
import { QUEST_DATABASE } from '../../../../../shared/content/generated/quests.gen';
import { prefersReducedMotion } from '../../../../systems/accessibility/motionPrefs';
import { NpcQuestRewards } from '../NpcQuestRewards';
import { QuestActionButton, QuestObjectiveList, QuestTurnInLine, questCardState, type QuestCardActions } from '../QuestCard';
import { QuestGuidance } from '../guidance/QuestGuidance';
import type { PlayerWhereabouts } from '../guidance/playerWhereabouts';
import { TomeFolio } from '../../../primitives/tome';
import { QuestPath } from './QuestPath';
import './questCodex.css';

const STATE_LABEL = { available: 'Offered', active: 'In progress', ready: 'Ready to turn in', completed: 'Completed' } as const;

interface QuestCodexPageProps {
  quest: Quest;
  /** featured: offered or under way, with its action. teaser: still locked, display only. */
  mode: 'featured' | 'teaser';
  playerLevel: number;
  you?: PlayerWhereabouts | null;
  /** Featured mode: the same action owner as the ordinary cards. */
  actions?: Omit<QuestCardActions, 'playerLevel'>;
}

/** "A", "A and B", "A, B and C". */
function joinNames(names: readonly string[]): string {
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * What still stands between the player and a locked featured quest: every
 * direct prerequisite not yet done ("do X and Y first"), then the level.
 */
export function teaserUnlockLines(quest: Quest, playerLevel: number): string[] {
  const lines: string[] = [];
  const open = (quest.prerequisites ?? [])
    .filter(id => quest.path?.find(step => step.id === id)?.state !== 'done');
  if (open.length > 0) lines.push(`Complete ${joinNames(open.map(id => QUEST_DATABASE[id]?.name ?? id))} to unlock`);
  if (playerLevel < quest.requiredLevel) lines.push(`Reach level ${quest.requiredLevel}`);
  return lines;
}

/**
 * A featured quest as the page's folio (plan 10 §2.2, lane M): plaque, wax
 * seal, illuminated title with a drop cap and one gold shimmer, its path,
 * then the same guidance, objectives, rewards and action as an ordinary entry.
 * Ready to turn in, the action comes first and the story folds away.
 */
export const QuestCodexPage: React.FC<QuestCodexPageProps> = ({ quest, mode, playerLevel, you, actions }) => {
  const teaser = mode === 'teaser';
  const state = teaser ? 'locked' : questCardState(quest);
  const unlock = teaser ? teaserUnlockLines(quest, playerLevel) : [];
  const ready = state === 'ready';
  return <TomeFolio className="npc-codex" aria-label={`Featured quest: ${quest.title}`}
    data-codex-quest={quest.id} data-codex-state={state} data-reduce-motion={prefersReducedMotion() || undefined}
    plaque={teaser ? 'A featured quest awaits' : 'Featured quest'} seal={teaser ? 'locked' : 'open'}
    bookmark={!teaser} muted={teaser}>
    <header className="npc-codex-header">
      <span className="npc-codex-kicker">Level {quest.requiredLevel} · {teaser ? 'Locked' : STATE_LABEL[state as keyof typeof STATE_LABEL]}</span>
      <h3 className="npc-codex-title">{quest.title}</h3>
    </header>
    {ready ? <>
      {/* Ready to turn in: the action leads; the story, path and map fold beneath it (plan 13 §2). */}
      <div className="npc-codex-body npc-codex-body--ready">
        <QuestTurnInLine quest={quest} />
        <QuestObjectiveList quest={quest} />
        <NpcQuestRewards quest={quest} />
        {actions && <QuestActionButton quest={quest} playerLevel={playerLevel} {...actions} />}
      </div>
      <details className="npc-quest-story npc-codex-story">
        <summary>Story, path &amp; map</summary>
        <p className="npc-codex-prose">{quest.description}</p>
        {quest.path && <QuestPath steps={quest.path} />}
        <QuestGuidance quest={quest} you={you} />
      </details>
    </> : <>
      <p className="npc-codex-prose">{quest.description}</p>
      {teaser && <ul className="npc-codex-unlock" aria-label="To unlock">
        {unlock.map(line => <li key={line}>{line}</li>)}
      </ul>}
      {quest.path && <QuestPath steps={quest.path} />}
      {!teaser && actions && <div className="npc-codex-body">
        {!quest.isCompleted && <QuestGuidance quest={quest} you={you} />}
        <QuestTurnInLine quest={quest} />
        <QuestObjectiveList quest={quest} />
        <NpcQuestRewards quest={quest} />
      </div>}
      {/* A child of the folio body, not of the codex body: the action pins to
          the foot of the page for the whole folio, so Accept is on screen
          from the plaque down (stream note 51). */}
      {!teaser && actions && <QuestActionButton quest={quest} playerLevel={playerLevel} {...actions} />}
    </>}
  </TomeFolio>;
};
